/**
 * MapOverview Component
 *
 * A comprehensive map interface for displaying project locations, excavation sites,
 * and related GIS layers with filtering, statistics, and interactive controls.
 *
 * Features:
 * - Interactive map with multiple base layers
 * - Project and excavation point markers
 * - Real-time filtering and search
 * - Statistics dashboard
 * - Responsive design for mobile and desktop
 * - Buffer zones and connection lines
 *
 * @component
 * @returns {JSX.Element} The MapOverview component
 */

import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { MapContainer, TileLayer, Circle, Polyline } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./cluster-styles.css";
import { MapPin, Filter, Map, Menu, X } from "lucide-react";

// API and utilities
import api from "../../../../../utils/api";

// Components
import { DropdownFilter } from "../../components/ui/DropdownFilter";
import { Button } from "../../components/AdminSharedComponents";
import MapControls from "./MapControls";
import StatisticsCards from "./StatisticsCards";
import Sidebar from "./Sidebar";
import ClusteredMarkers from "./ClusteredMarkers";

// Utils and constants
import {
  getProjectIcon,
  getExcavationIcon,
  getStatusColor,
  calculateStats,
  generateConnections,
  calculateMapCenter,
  BASE_LAYERS,
} from "./utils";

// Constants
const DEFAULT_MAP_CENTER = [-2.5489, 118.0149];
const DEFAULT_ZOOM = 5;
const BUFFER_RADIUS_DEFAULT = 500;
const MOBILE_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;

// API endpoints
const API_ENDPOINTS = {
  PROJECTS_LOCATIONS: "/api/projects/locations",
  EXCAVATION_POINTS: "/api/excavation-points",
};

// Default filter state
const DEFAULT_FILTERS = {
  projectStatus: "all",
  excavationStatus: "all",
  showProjects: true,
  showExcavations: true,
  showBuffers: false,
  showConnections: false,
  bufferRadius: BUFFER_RADIUS_DEFAULT,
};

// Map styling constants
const MAP_STYLES = {
  CONNECTION_LINE: {
    color: "#3b82f6",
    weight: 3,
    opacity: 0.8,
    dashArray: "5, 10",
  },
  CONNECTION_LINE_MOBILE: {
    color: "#3b82f6",
    weight: 2,
    opacity: 0.8,
    dashArray: "5, 10",
  },
  BUFFER_CIRCLE: {
    color: "#3b82f6",
    fillColor: "#3b82f6",
    fillOpacity: 0.1,
    weight: 1,
  },
};

// UI text constants
const UI_TEXT = {
  LOADING: "Memuat peta...",
  ERROR_RELOAD: "Coba Lagi",
  MAP_OVERVIEW: "Map Overview",
  DESCRIPTION:
    "Displaying project locations, excavation sites, and related GIS layers",
  OPEN_FILTERS: "Open Filters",
  CLOSE_FILTERS: "Close Filters",
  MAP_VIEW: "Map View",
  SELECT_MAP_VIEW: "Select Map View",
  VIEW_DETAILS: "Lihat Detail",
  PROJECT_DETAIL: "Project Detail",
  EXCAVATION_DETAIL: "Excavation Detail",
  STATUS: "Status:",
  PIC: "PIC:",
  PROJECT: "Project:",
  REPORTS: "Reports:",
  LAT: "Lat:",
  LONG: "Long:",
  PROJECTS: "Projects",
  EXCAVATIONS: "Excavations",
  ACTIVE: "Active",
  COMPLETED: "Completed",
};

/**
 * StatusBadge Component
 *
 * A reusable badge component for displaying status with appropriate styling.
 *
 * @param {Object} props - Component props
 * @param {string} props.status - The status to display
 * @returns {JSX.Element} Status badge element
 */
const StatusBadge = React.memo(({ status }) => (
  <span
    className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(
      status
    )}`}
    role="status"
    aria-label={`Status: ${status || "Unknown"}`}
  >
    {status || "Unknown"}
  </span>
));

StatusBadge.displayName = "StatusBadge";

/**
 * MobileHeader Component
 *
 * Responsive header component for the map overview with mobile and desktop layouts.
 * Includes sidebar toggle, layer selector, and statistics display.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.sidebarOpen - Whether the sidebar is open
 * @param {Function} props.setSidebarOpen - Function to toggle sidebar
 * @param {Object} props.selectedBaseLayer - Currently selected base layer
 * @param {Function} props.setSelectedBaseLayer - Function to set base layer
 * @param {Array} props.baseLayerOptions - Available base layer options
 * @param {Object} props.stats - Statistics data to display
 * @returns {JSX.Element} Mobile header element
 */
const MobileHeader = React.memo(
  ({
    sidebarOpen,
    setSidebarOpen,
    selectedBaseLayer,
    setSelectedBaseLayer,
    baseLayerOptions,
    stats,
  }) => {
    const [showMobileStats, setShowMobileStats] = useState(false);

    const handleSidebarToggle = useCallback(() => {
      setSidebarOpen(!sidebarOpen);
    }, [sidebarOpen, setSidebarOpen]);

    const handleStatsToggle = useCallback(() => {
      setShowMobileStats(!showMobileStats);
    }, [showMobileStats]);

    const handleLayerChange = useCallback(
      (val) => {
        const layer = BASE_LAYERS.find((l) => l.name === val);
        setSelectedBaseLayer(layer);
      },
      [setSelectedBaseLayer]
    );

    return (
      <header className="bg-white border-b border-gray-200 shadow-sm z-10 relative">
        {/* Main Header */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left - Menu & Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleSidebarToggle}
                className="p-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors lg:hidden"
                aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              >
                {sidebarOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
              <div className="flex items-center gap-2">
                <Map className="h-5 w-5 text-primary" aria-hidden="true" />
                <h1 className="text-lg sm:text-xl font-bold text-primary">
                  {UI_TEXT.MAP_OVERVIEW}
                </h1>
              </div>
            </div>

            {/* Right - Stats Toggle & Layer Selector */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleStatsToggle}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors sm:hidden"
                aria-label="Toggle statistics"
              >
                <MapPin className="w-4 h-4" />
              </button>

              {/* Base Layer Selector - Hidden on small screens */}
              <div className="hidden sm:block">
                <DropdownFilter
                  options={baseLayerOptions}
                  value={selectedBaseLayer.name}
                  onChange={handleLayerChange}
                  placeholder={UI_TEXT.MAP_VIEW}
                />
              </div>

              {/* Desktop Filter Button */}
              <Button
                variant={sidebarOpen ? "secondary" : "primary"}
                onClick={handleSidebarToggle}
                icon={Filter}
                className="relative hidden lg:flex"
                aria-label={
                  sidebarOpen ? UI_TEXT.CLOSE_FILTERS : UI_TEXT.OPEN_FILTERS
                }
              >
                {sidebarOpen ? UI_TEXT.CLOSE_FILTERS : UI_TEXT.OPEN_FILTERS}
              </Button>
            </div>
          </div>

          {/* Description - Hidden on mobile */}
          <p className="text-gray-600 text-xs sm:text-sm mt-2 hidden sm:block">
            {UI_TEXT.DESCRIPTION}
          </p>
        </div>

        {/* Mobile Stats Dropdown */}
        {showMobileStats && (
          <div className="border-t border-gray-200 px-4 py-4 bg-gradient-to-r from-gray-50 to-gray-100/50 sm:hidden">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/20 shadow-sm">
                <div className="text-sm text-gray-600 font-medium">
                  {UI_TEXT.PROJECTS}
                </div>
                <div className="text-xl font-bold text-primary mt-1">
                  {stats.totalProjects}
                </div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/20 shadow-sm">
                <div className="text-sm text-gray-600 font-medium">
                  {UI_TEXT.EXCAVATIONS}
                </div>
                <div className="text-xl font-bold text-orange-600 mt-1">
                  {stats.totalExcavations}
                </div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/20 shadow-sm">
                <div className="text-sm text-gray-600 font-medium">
                  {UI_TEXT.ACTIVE}
                </div>
                <div className="text-xl font-bold text-green-600 mt-1">
                  {stats.activeProjects}
                </div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/20 shadow-sm">
                <div className="text-sm text-gray-600 font-medium">
                  {UI_TEXT.COMPLETED}
                </div>
                <div className="text-xl font-bold text-blue-600 mt-1">
                  {stats.completedProjects}
                </div>
              </div>
            </div>

            {/* Mobile Base Layer Selector */}
            <div className="mt-3">
              <DropdownFilter
                options={baseLayerOptions}
                value={selectedBaseLayer.name}
                onChange={handleLayerChange}
                placeholder={UI_TEXT.SELECT_MAP_VIEW}
              />
            </div>
          </div>
        )}

        {/* Desktop Stats */}
        <div className="hidden sm:block border-t border-gray-200 px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100/50">
          <StatisticsCards stats={stats} />
        </div>
      </header>
    );
  }
);

MobileHeader.displayName = "MobileHeader";

/**
 * MapOverview Component
 *
 * Main component for displaying interactive map with projects and excavations.
 * Handles data loading, filtering, and map interactions.
 *
 * @returns {JSX.Element} The MapOverview component
 */
function MapOverview() {
  const mapRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [excavations, setExcavations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchTerm, setSearchTerm] = useState("");
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [zoom] = useState(DEFAULT_ZOOM);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [selectedBaseLayer, setSelectedBaseLayer] = useState(BASE_LAYERS[0]);
  const [layerDropdownOpen, setLayerDropdownOpen] = useState(false);

  const baseLayerOptions = useMemo(
    () =>
      BASE_LAYERS.map((layer) => ({
        value: layer.name,
        label: layer.name,
      })),
    []
  );

  /**
   * Transform project coordinates from GeoJSON to lat/lng format
   * @param {Array} projects - Array of project data
   * @returns {Array} Transformed projects with lat/lng coordinates
   */
  const transformProjectCoordinates = useCallback((projects) => {
    return (projects || []).map((project) => {
      if (project.coordinates && project.coordinates.coordinates) {
        return {
          ...project,
          coordinates: {
            lat: project.coordinates.coordinates[1],
            lng: project.coordinates.coordinates[0],
          },
        };
      }
      return project;
    });
  }, []);

  /**
   * Load data from API endpoints
   */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [projectsRes, excavationsRes] = await Promise.all([
        api.get(API_ENDPOINTS.PROJECTS_LOCATIONS),
        api.get(API_ENDPOINTS.EXCAVATION_POINTS),
      ]);

      const transformedProjects = transformProjectCoordinates(projectsRes.data);
      setProjects(transformedProjects);

      // Handle excavation points API response structure
      const excavationsData =
        excavationsRes.data?.data || excavationsRes.data || [];
      setExcavations(Array.isArray(excavationsData) ? excavationsData : []);

      const center = calculateMapCenter(
        projectsRes.data || [],
        excavationsData || []
      );
      setMapCenter(center);
    } catch (err) {
      const errorMessage = "Gagal memuat data peta";
      setError(errorMessage);
      console.error("Error loading map data:", err);
    } finally {
      setLoading(false);
    }
  }, [transformProjectCoordinates]);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Handle window resize events for responsive sidebar behavior
   */
  const handleResize = useCallback(() => {
    if (window.innerWidth >= DESKTOP_BREAKPOINT && sidebarOpen) {
      // Keep sidebar open on desktop
    } else if (window.innerWidth < DESKTOP_BREAKPOINT && sidebarOpen) {
      // Auto-close on mobile when resizing
      setSidebarOpen(false);
    }
  }, [sidebarOpen]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setLayerDropdownOpen(false);
    if (layerDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [layerDropdownOpen]);

  // Filtered data
  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];

    return projects.filter((p) => {
      const matchesStatus =
        filters.projectStatus === "all" || p.status === filters.projectStatus;
      const matchesSearch =
        searchTerm === "" ||
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.pic?.toLowerCase().includes(searchTerm.toLowerCase());
      return (
        matchesStatus &&
        matchesSearch &&
        p.coordinates &&
        p.coordinates.lat &&
        p.coordinates.lng
      );
    });
  }, [projects, filters.projectStatus, searchTerm]);

  const filteredExcavations = useMemo(() => {
    if (!Array.isArray(excavations)) return [];

    return excavations.filter((e) => {
      const matchesStatus =
        filters.excavationStatus === "all" ||
        e.status === filters.excavationStatus;
      const matchesSearch =
        searchTerm === "" ||
        e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.Project?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch && e.coordinates;
    });
  }, [excavations, filters.excavationStatus, searchTerm]);

  // Stats
  const stats = useMemo(
    () =>
      calculateStats(
        Array.isArray(projects) ? projects : [],
        Array.isArray(excavations) ? excavations : []
      ),
    [projects, excavations]
  );

  // Connections
  const connections = useMemo(
    () =>
      generateConnections(
        filteredProjects,
        filteredExcavations,
        filters.showConnections
      ),
    [filteredProjects, filteredExcavations, filters.showConnections]
  );

  // Prepare markers data for clustering
  const projectMarkersData = useMemo(() => {
    console.log(
      `Preparing ${filteredProjects.length} project markers for clustering`
    );

    // Check for duplicate coordinates
    const coordinates = filteredProjects.map(
      (p) => `${p.coordinates.lat.toFixed(6)}_${p.coordinates.lng.toFixed(6)}`
    );
    const uniqueCoordinates = new Set(coordinates);

    if (coordinates.length !== uniqueCoordinates.size) {
      console.warn(
        `Found duplicate project coordinates: ${coordinates.length} total, ${uniqueCoordinates.size} unique`
      );
      console.log(
        "Duplicate coordinates:",
        coordinates.filter(
          (coord, index) => coordinates.indexOf(coord) !== index
        )
      );
    }

    return filteredProjects.map((project) => ({
      position: [project.coordinates.lat, project.coordinates.lng],
      icon: getProjectIcon(project.status),
      popup: `
        <div class="min-w-56 sm:min-w-64 bg-white rounded-lg shadow-lg border border-gray-100">
          <div class="p-4">
            <div class="flex items-center gap-3 mb-3">
              <div class="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 class="font-semibold text-gray-900 text-base truncate">${
                project.name
              }</h3>
            </div>
            
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Status</span>
                <span class="px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(
                  project.status
                )}">${project.status || "Unknown"}</span>
              </div>
              
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">PIC</span>
                <span class="text-sm font-medium text-gray-900 truncate max-w-32">${
                  project.pic || "N/A"
                }</span>
              </div>
              
              <div class="pt-3 border-t border-gray-100">
                <div class="grid grid-cols-2 gap-2 text-xs text-gray-500">
                  <div>
                    <span class="font-medium">Lat:</span>
                    <div class="font-mono">${project.coordinates.lat.toFixed(
                      6
                    )}</div>
                  </div>
                  <div>
                    <span class="font-medium">Lng:</span>
                    <div class="font-mono">${project.coordinates.lng.toFixed(
                      6
                    )}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="mt-4 pt-3 border-t border-gray-100">
              <a href="/admin/projects/show/${project.id}" 
                 class="block w-full px-4 py-2.5 bg-primary text-white text-center rounded-lg hover:bg-primary/90 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md">
                ${UI_TEXT.VIEW_DETAILS}
              </a>
            </div>
          </div>
        </div>
      `,
      eventHandlers: {
        mouseover: (e, marker) => {
          marker.openPopup();
        },
        click: (e, marker) => {
          marker.openPopup();
          const map = marker._map;
          if (map) {
            // Smooth zoom animation
            map.flyTo(marker.getLatLng(), 18, {
              animate: true,
              duration: 1.5, // 1.5 seconds for smooth transition
              easeLinearity: 0.1, // Smoother easing
            });
          }
        },
      },
    }));
  }, [filteredProjects]);

  const excavationMarkersData = useMemo(() => {
    console.log(
      `Preparing ${filteredExcavations.length} excavation markers for clustering`
    );

    // Check for duplicate coordinates
    const coordinates = filteredExcavations.map(
      (e) => `${e.coordinates.lat.toFixed(6)}_${e.coordinates.lng.toFixed(6)}`
    );
    const uniqueCoordinates = new Set(coordinates);

    if (coordinates.length !== uniqueCoordinates.size) {
      console.warn(
        `Found duplicate excavation coordinates: ${coordinates.length} total, ${uniqueCoordinates.size} unique`
      );
      console.log(
        "Duplicate coordinates:",
        coordinates.filter(
          (coord, index) => coordinates.indexOf(coord) !== index
        )
      );
    }

    return filteredExcavations.map((excavation) => ({
      position: [excavation.coordinates.lat, excavation.coordinates.lng],
      icon: getExcavationIcon(excavation.status),
      popup: `
        <div class="min-w-56 sm:min-w-64 bg-white rounded-lg shadow-lg border border-gray-100">
          <div class="p-4">
            <div class="flex items-center gap-3 mb-3">
              <div class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 class="font-semibold text-gray-900 text-base truncate">${
                excavation.name
              }</h3>
            </div>
            
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Status</span>
                <span class="px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(
                  excavation.status
                )}">${excavation.status || "Unknown"}</span>
              </div>
              
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Project</span>
                <span class="text-sm font-medium text-gray-900 truncate max-w-32">${
                  excavation.Project?.name || "N/A"
                }</span>
              </div>
              
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Reports</span>
                <span class="text-sm font-medium text-gray-900">${
                  excavation.reports?.length || 0
                }</span>
              </div>
              
              <div class="pt-3 border-t border-gray-100">
                <div class="grid grid-cols-2 gap-2 text-xs text-gray-500">
                  <div>
                    <span class="font-medium">Lat:</span>
                    <div class="font-mono">${excavation.coordinates.lat.toFixed(
                      6
                    )}</div>
                  </div>
                  <div>
                    <span class="font-medium">Lng:</span>
                    <div class="font-mono">${excavation.coordinates.lng.toFixed(
                      6
                    )}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="mt-4 pt-3 border-t border-gray-100">
              <div class="flex gap-2">
                <a href="/admin/projects/show/${excavation.Project?.id}" 
                   class="flex-1 px-3 py-2.5 bg-gray-100 text-gray-800 text-center rounded-lg hover:bg-gray-200 transition-all duration-200 text-xs font-medium">
                  ${UI_TEXT.PROJECT_DETAIL}
                </a>
                <a href="/admin/projects/excavation/show/${excavation.id}" 
                   class="flex-1 px-3 py-2.5 bg-primary text-white text-center rounded-lg hover:bg-primary/90 transition-all duration-200 text-xs font-medium shadow-sm hover:shadow-md">
                  ${UI_TEXT.EXCAVATION_DETAIL}
                </a>
              </div>
            </div>
          </div>
        </div>
      `,
      eventHandlers: {
        click: (e, marker) => {
          marker.openPopup();
          const map = marker._map;
          if (map) {
            // Smooth zoom animation
            map.flyTo(marker.getLatLng(), 18, {
              animate: true,
              duration: 1.5, // 1.5 seconds for smooth transition
              easeLinearity: 0.1, // Smoother easing
            });
          }
        },
        mouseover: (e, marker) => {
          marker.openPopup();
        },
      },
    }));
  }, [filteredExcavations]);

  /**
   * Refresh data from API
   */
  const handleRefreshData = useCallback(async () => {
    try {
      const [projectsRes, excavationsRes] = await Promise.all([
        api.get(API_ENDPOINTS.PROJECTS_LOCATIONS),
        api.get(API_ENDPOINTS.EXCAVATION_POINTS),
      ]);

      const transformedProjects = transformProjectCoordinates(projectsRes.data);
      setProjects(transformedProjects);

      // Handle excavation points API response structure
      const excavationsData =
        excavationsRes.data?.data || excavationsRes.data || [];
      setExcavations(Array.isArray(excavationsData) ? excavationsData : []);
    } catch (err) {
      console.error("Error refreshing data:", err);
    }
  }, [transformProjectCoordinates]);

  /**
   * Loading state component
   */
  const LoadingState = () => (
    <div className="w-full h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center px-4">
        <div
          className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"
          role="status"
          aria-label="Loading"
        />
        <p className="text-gray-600 text-sm sm:text-base">{UI_TEXT.LOADING}</p>
      </div>
    </div>
  );

  /**
   * Error state component
   */
  const ErrorState = () => (
    <div className="w-full h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center text-red-600 max-w-md">
        <p className="text-lg font-semibold mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm sm:text-base"
          aria-label="Reload page"
        >
          {UI_TEXT.ERROR_RELOAD}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState />;
  }

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50">
      {/* Mobile & Desktop Header */}
      <MobileHeader
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        selectedBaseLayer={selectedBaseLayer}
        setSelectedBaseLayer={setSelectedBaseLayer}
        baseLayerOptions={baseLayerOptions}
        stats={stats}
      />

      <div className="flex flex-1 relative overflow-hidden">
        {/* Sidebar - Responsive */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          filters={filters}
          setFilters={setFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          projects={projects}
          excavations={excavations}
        />

        {/* Sidebar Backdrop for Mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Map Container */}
        <div className="flex-1 relative">
          <MapContainer
            center={mapCenter}
            zoom={zoom}
            style={{ height: "100%", width: "100%" }}
            className="z-0"
            zoomControl={false}
          >
            <TileLayer
              url={selectedBaseLayer.url}
              attribution={selectedBaseLayer.attribution}
            />

            {/* Projects - Clustered */}
            {filters.showProjects && (
              <ClusteredMarkers
                markers={projectMarkersData}
                enabled={true}
                clusterOptions={{
                  maxClusterRadius: 80,
                  disableClusteringAtZoom: 18,
                }}
              />
            )}

            {/* Excavations - Clustered */}
            {filters.showExcavations && (
              <ClusteredMarkers
                markers={excavationMarkersData}
                enabled={true}
                clusterOptions={{
                  maxClusterRadius: 60,
                  disableClusteringAtZoom: 16,
                }}
              />
            )}

            {/* Connections */}
            {filters.showConnections &&
              connections.map((conn) => (
                <Polyline
                  key={conn.id}
                  positions={conn.positions}
                  pathOptions={
                    window.innerWidth < MOBILE_BREAKPOINT
                      ? MAP_STYLES.CONNECTION_LINE_MOBILE
                      : MAP_STYLES.CONNECTION_LINE
                  }
                />
              ))}

            {/* Buffers */}
            {filters.showBuffers &&
              filteredProjects.map((project) => (
                <Circle
                  key={`buffer-${project.id}`}
                  center={[project.coordinates.lat, project.coordinates.lng]}
                  radius={filters.bufferRadius}
                  pathOptions={MAP_STYLES.BUFFER_CIRCLE}
                />
              ))}
          </MapContainer>

          {/* Map Controls - Responsive positioning */}
          <MapControls
            showSidebar={sidebarOpen}
            setShowSidebar={setSidebarOpen}
            resetMapView={() => {
              if (mapRef.current) {
                mapRef.current.setView(mapCenter, zoom);
              }
            }}
            hoveredButton={hoveredButton}
            setHoveredButton={setHoveredButton}
            selectedBaseLayer={selectedBaseLayer}
            onLayerChange={(layerName) => {
              const layer = BASE_LAYERS.find((l) => l.name === layerName);
              if (layer) {
                setSelectedBaseLayer(layer);
              }
            }}
            onRefresh={handleRefreshData}
          />
        </div>
      </div>
    </div>
  );
}

export default MapOverview;
